import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import { api } from "@/lib/api";
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { Calendar, MapPin, FileText, ExternalLink, Mail, Loader2, CheckCircle2 } from "lucide-react";

export default function OpportunityDetail() {
  const { slug } = useParams();
  const [o, setO] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", company: "", phone: "", message: "" });

  const load = useCallback(() => api.get(`/opportunities/${slug}`).then((r) => setO(r.data)).catch(() => setO(false)), [slug]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post(`/opportunities/${slug}/apply`, form);
      setSubmitted(true);
      if (r.data?.checkout_url) {
        toast.success("Redirection vers le paiement…");
        setTimeout(() => { window.location.href = r.data.checkout_url; }, 800);
      } else {
        toast.success("Candidature reçue ! Notre équipe vous recontactera.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSubmitting(false); }
  };

  if (o === false) return <PublicLayout><div className="mx-auto max-w-3xl px-6 py-20 text-center"><h1 className="font-serif text-3xl">Opportunité introuvable</h1></div></PublicLayout>;
  if (!o) return <PublicLayout><div className="mx-auto max-w-3xl px-6 py-20 text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-wine-500" /></div></PublicLayout>;

  const price = parseFloat(o.ticket_price || 0);

  return (
    <PublicLayout>
      <section className="mx-auto max-w-7xl px-6 py-10">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
          <div className="min-w-0">
            <div className="aspect-[16/9] overflow-hidden rounded-3xl bg-neutral-50">
              {o.image ? <img src={resolveUrl(o.image)} alt={o.title} className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-8xl">🚀</div>}
            </div>
            <h1 className="mt-6 font-serif text-4xl text-navy md:text-5xl">{o.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-neutral-600">
              {o.deadline && (<span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> Deadline : {(o.deadline || "").slice(0, 10)}</span>)}
              {o.location && (<span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {o.location}</span>)}
              {o.organizer && (<span className="text-xs italic">par {o.organizer}</span>)}
            </div>

            {o.description && (
              <div className="mt-6 max-w-none text-neutral-700 leading-relaxed [&_p]:my-4 [&_p:first-child]:mt-0 [&_h1]:font-serif [&_h1]:text-3xl [&_h1]:text-navy [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:text-navy [&_h2]:mt-5 [&_h2]:mb-2 [&_h3]:font-serif [&_h3]:text-xl [&_h3]:text-navy [&_strong]:font-bold [&_strong]:text-navy [&_a]:text-wine-600 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1" data-testid="opportunity-description">
                <ReactMarkdown remarkPlugins={[remarkBreaks]}>{o.description}</ReactMarkdown>
              </div>
            )}

            {(o.pdf_url || o.link_external) && (
              <div className="mt-6 flex flex-wrap gap-3">
                {o.pdf_url && <a href={resolveUrl(o.pdf_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 border-wine-300 bg-white px-5 py-2.5 text-sm font-bold text-wine-700 hover:bg-wine-50" data-testid="pdf-link"><FileText className="h-4 w-4" /> Télécharger le dossier (PDF)</a>}
                {o.link_external && <a href={o.link_external} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 border-neutral-300 bg-white px-5 py-2.5 text-sm font-bold hover:bg-neutral-50" data-testid="external-link"><ExternalLink className="h-4 w-4" /> Site externe</a>}
              </div>
            )}
          </div>

          <aside className="lg:sticky lg:top-24 self-start rounded-2xl bg-gradient-to-br from-wine-50 to-amber-50 p-6 shadow-sm">
            {submitted ? (
              <div className="text-center" data-testid="opportunity-thank-you">
                <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
                <h3 className="mt-2 font-serif text-2xl text-navy">Merci !</h3>
                <p className="mt-2 text-sm text-neutral-700">Votre candidature a bien été enregistrée. {price > 0 ? "Redirection vers le paiement…" : "Notre équipe vous recontactera très prochainement."}</p>
              </div>
            ) : (
              <>
                <h3 className="font-serif text-xl font-semibold text-navy">{price > 0 ? `Candidater · ${price} €` : "Candidater"}</h3>
                <p className="mt-1 text-xs text-neutral-600">{o.ticket_name || "Votre dossier sera transmis à l'équipe La WineTech."}</p>
                <form onSubmit={submit} className="mt-4 space-y-2.5">
                  <div className="grid grid-cols-2 gap-2">
                    <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Prénom" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="apply-first-name" />
                    <input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Nom" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="apply-last-name" />
                  </div>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email professionnel" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="apply-email" />
                  <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Entreprise" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="apply-company" />
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone (optionnel)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="apply-phone" />
                  <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Pitch / motivation" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="apply-message" />
                  <button type="submit" disabled={submitting} className="w-full rounded-full bg-wine-500 px-5 py-3 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-60" data-testid="apply-submit">
                    {submitting ? <Loader2 className="inline h-4 w-4 animate-spin" /> : (price > 0 ? `Candidater & Payer ${price} €` : "Envoyer ma candidature")}
                  </button>
                </form>
                {o.contact_email && (
                  <p className="mt-3 text-center text-xs text-neutral-500">
                    <Mail className="mr-1 inline h-3 w-3" /> Question ? <a href={`mailto:${o.contact_email}`} className="text-wine-600 underline">{o.contact_email}</a>
                  </p>
                )}
              </>
            )}
          </aside>
        </div>
      </section>
    </PublicLayout>
  );
}
